/**
 * Cockpit Phase 6 — the CockpitSpec runtime renderer (COCKPIT-PLAN §4).
 *
 * Interprets a validated CockpitSpec tree → DOM using ONLY the closed
 * cockpit-library component renderers. This is the "compile (fixed renderer, no
 * dangerouslySetInnerHTML)" layer of the 4-layer "only catalog components"
 * enforcement — a node whose component is not in the renderer registry renders a
 * visible "unknown component" placeholder, NEVER raw HTML.
 *
 * The ENTIRE catalog ships a REAL renderer here — the Phase-6 core 8, the
 * earlier-completed layout/data/input set, AND the comprehensive palette
 * (Chart — line/area/bar/pie/donut/gauge/sparkline/scatter via Recharts; MapView
 * — air-gap-safe inline-SVG geospatial, NO tile server; Icon — lucide-react by
 * name; Badge, ProgressBar, Stat, Divider, ButtonGroup, ToggleGroup, TreeList).
 * This is the AUTHORING-PREVIEW path (Recharts + lucide + shadcn are available);
 * the in-iframe runtime path (ai-core/cockpit-spec-renderer) hand-draws the same
 * charts/maps/icons as inline SVG so it stays air-gap-safe + dependency-free.
 *
 * Data access goes through the host bridge (`window.argusLive.fetchData` /
 * `executeAction`) which the iframe entry installs — but for the authoring live
 * preview + tests, an optional `dataResolver` / `onAction` prop lets the host
 * drive the renderer directly. Tailwind + the status palette (critical=red,
 * warning=amber, normal=slate, success=green) only; the single `style={{width}}`
 * on the ProgressBar fill is a dynamic geometry value (a percentage), not a
 * style injection.
 */

import type React from 'react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  type CockpitSpec,
  type CockpitNode,
  type CockpitStatusTone,
  type ResolvedCockpitPage,
  getCockpitPages,
  isCockpitComponent,
} from '@argusai/shared';
import {
  Search as SearchIcon,
  Circle as CircleIcon,
  Bell as BellIcon,
  Inbox as InboxIcon,
  Bold as BoldIcon,
  Italic as ItalicIcon,
  List as ListIcon,
  ChevronRight as ChevronRightIcon,
  ChevronDown as ChevronDownIcon,
  type LucideIcon,
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  RadialBarChart,
  RadialBar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend as RechartsLegend,
} from 'recharts';

/* ─── Host bridge contract (the iframe runtime) ─────────────────────────── */

export interface CockpitRendererBridge {
  /** Resolve a binding id → its current data (mirrors window.argusLive.fetchData). */
  fetchBinding?: (bindingId: string) => Promise<unknown>;
  /** Trigger a declared, approval-gated action (mirrors executeAction). */
  executeAction?: (actionId: string, input?: Record<string, unknown>) => void;
}

export interface CockpitRendererProps {
  spec: CockpitSpec;
  bridge?: CockpitRendererBridge;
  /** Theme — inherited from the host. */
  theme?: 'light' | 'dark';
}

/* ─── Status palette (COCKPIT-PLAN §4) ──────────────────────────────────── */

const TONE_CLASSES: Record<CockpitStatusTone, { text: string; bg: string; ring: string }> = {
  normal: { text: 'text-slate-700 dark:text-slate-200', bg: 'bg-slate-100 dark:bg-slate-800', ring: 'ring-slate-300 dark:ring-slate-600' },
  success: { text: 'text-green-700 dark:text-green-300', bg: 'bg-green-50 dark:bg-green-950', ring: 'ring-green-300 dark:ring-green-700' },
  warning: { text: 'text-amber-700 dark:text-amber-300', bg: 'bg-amber-50 dark:bg-amber-950', ring: 'ring-amber-300 dark:ring-amber-700' },
  critical: { text: 'text-red-700 dark:text-red-300', bg: 'bg-red-50 dark:bg-red-950', ring: 'ring-red-300 dark:ring-red-700' },
  info: { text: 'text-blue-700 dark:text-blue-300', bg: 'bg-blue-50 dark:bg-blue-950', ring: 'ring-blue-300 dark:ring-blue-700' },
};

function tone(t: unknown): CockpitStatusTone {
  return t === 'success' || t === 'warning' || t === 'critical' || t === 'info'
    ? t
    : 'normal';
}

/* ─── Binding data hook ─────────────────────────────────────────────────── */

/**
 * Resolve a binding id to data via the host bridge. Returns { data, loading,
 * error }. With no bridge (or no binding ref), returns idle.
 */
function useBindingData(
  bindingId: string | undefined,
  bridge: CockpitRendererBridge | undefined,
): { data: unknown; loading: boolean; error: string | null } {
  const [state, setState] = useState<{ data: unknown; loading: boolean; error: string | null }>({
    data: undefined,
    loading: false,
    error: null,
  });
  useEffect(() => {
    let cancelled = false;
    if (!bindingId || !bridge?.fetchBinding) {
      setState({ data: undefined, loading: false, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    bridge
      .fetchBinding(bindingId)
      .then((d) => {
        if (!cancelled) setState({ data: d, loading: false, error: null });
      })
      .catch((e: unknown) => {
        if (!cancelled)
          setState({ data: undefined, loading: false, error: e instanceof Error ? e.message : 'Failed to load' });
      });
    return () => {
      cancelled = true;
    };
  }, [bindingId, bridge]);
  return state;
}

function getPath(obj: unknown, path?: string): unknown {
  if (!path) return obj;
  let cur: unknown = obj;
  for (const seg of path.split('.')) {
    if (cur && typeof cur === 'object') cur = (cur as Record<string, unknown>)[seg];
    else return undefined;
  }
  return cur;
}

/**
 * Resolve a subscribe binding and re-poll it on the host's adaptive cadence.
 * The host bridge (window.argusLive.fetchData) already debounces/adaptive-polls
 * upstream; here we re-fetch on a fixed visible interval so the "live" affordance
 * is real (a LiveIndicator that never updates is a lie). Falls back to a single
 * fetch when no interval is desired. With no binding/bridge, returns idle.
 */
function useSubscribeData(
  bindingId: string | undefined,
  bridge: CockpitRendererBridge | undefined,
  intervalMs = 15000,
): { data: unknown; lastUpdated: number | null; loading: boolean; error: string | null } {
  const [state, setState] = useState<{
    data: unknown;
    lastUpdated: number | null;
    loading: boolean;
    error: string | null;
  }>({ data: undefined, lastUpdated: null, loading: false, error: null });
  useEffect(() => {
    let cancelled = false;
    if (!bindingId || !bridge?.fetchBinding) {
      setState({ data: undefined, lastUpdated: null, loading: false, error: null });
      return;
    }
    const poll = () => {
      setState((s) => ({ ...s, loading: s.lastUpdated === null, error: null }));
      bridge
        .fetchBinding!(bindingId)
        .then((d) => {
          if (!cancelled) setState({ data: d, lastUpdated: Date.now(), loading: false, error: null });
        })
        .catch((e: unknown) => {
          if (!cancelled)
            setState((s) => ({
              ...s,
              loading: false,
              error: e instanceof Error ? e.message : 'Failed to load',
            }));
        });
    };
    poll();
    const id = setInterval(poll, Math.max(2000, intervalMs));
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [bindingId, bridge, intervalMs]);
  return state;
}

/**
 * Compact, monotonic "x ago" formatter. Avoids Intl.RelativeTimeFormat churn in
 * the hot render path; the cockpit just needs "just now / 3m / 2h / 4d".
 */
function relativeTime(ts: unknown, now: number = Date.now()): string {
  const t = typeof ts === 'number' ? ts : typeof ts === 'string' ? Date.parse(ts) : NaN;
  if (!Number.isFinite(t)) return typeof ts === 'string' ? ts : '';
  const diff = Math.max(0, now - t);
  const s = Math.floor(diff / 1000);
  if (s < 10) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/* Pill/chip tone classes — solid-ish chips for status pills + timeline dots. */
const PILL_CLASSES: Record<CockpitStatusTone, string> = {
  normal: 'bg-slate-100 text-slate-700 ring-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-600',
  success: 'bg-green-100 text-green-800 ring-green-300 dark:bg-green-950 dark:text-green-300 dark:ring-green-700',
  warning: 'bg-amber-100 text-amber-800 ring-amber-300 dark:bg-amber-950 dark:text-amber-300 dark:ring-amber-700',
  critical: 'bg-red-100 text-red-800 ring-red-300 dark:bg-red-950 dark:text-red-300 dark:ring-red-700',
  info: 'bg-blue-100 text-blue-800 ring-blue-300 dark:bg-blue-950 dark:text-blue-300 dark:ring-blue-700',
};
const DOT_CLASSES: Record<CockpitStatusTone, string> = {
  normal: 'bg-slate-400 dark:bg-slate-500',
  success: 'bg-green-500',
  warning: 'bg-amber-500',
  critical: 'bg-red-500',
  info: 'bg-blue-500',
};

/**
 * Hex equivalents of the status palette — Recharts (and inline SVG fills) need
 * concrete colours, not Tailwind classes. Mirrors the vanilla renderer's
 * TONE_PALETTE `.dot` values so the React + iframe charts look identical.
 */
const TONE_HEX: Record<CockpitStatusTone, string> = {
  normal: '#94a3b8',
  success: '#22c55e',
  warning: '#f59e0b',
  critical: '#ef4444',
  info: '#3b82f6',
};

function colorAt(colors: unknown, i: number): string {
  const arr = Array.isArray(colors) ? (colors as string[]) : [];
  const fallback: CockpitStatusTone[] = ['info', 'success', 'warning', 'critical', 'normal'];
  const key = (arr[i] as CockpitStatusTone) ?? fallback[i % fallback.length]!;
  return TONE_HEX[key] ?? TONE_HEX.info;
}

/* ─── Icon name → lucide component resolver ─────────────────────────────── */

/**
 * Map a curated cockpit icon name (kebab-case, e.g. "alert-triangle") to its
 * lucide-react component (PascalCase, e.g. `AlertTriangle`). Falls back to
 * `Circle` for any unmapped name so a bad name never crashes the render. This
 * keeps the React path visually consistent with the vanilla iframe path, which
 * hand-draws the same lucide geometry.
 */
function lucideForName(name: unknown): LucideIcon {
  if (typeof name !== 'string') return CircleIcon;
  const pascal = name
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join('');
  const map = LucideIcons as unknown as Record<string, LucideIcon>;
  return map[pascal] ?? CircleIcon;
}

const ICON_PX: Record<string, number> = { sm: 14, md: 20, lg: 28 };

/* ─── Core 8 renderers ──────────────────────────────────────────────────── */

type NodeProps = Record<string, unknown>;

function CockpitContainer({ node, bridge, theme }: { node: CockpitNode; bridge?: CockpitRendererBridge; theme?: 'light' | 'dark' }) {
  const p = node.props ?? {};
  return (
    <section role="main" aria-label={String(p.title ?? 'Cockpit')} className="flex flex-col gap-4 p-4 min-h-full bg-background text-foreground">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold tracking-tight">{String(p.title ?? '')}</h1>
        {p.subtitle ? <p className="text-sm text-muted-foreground">{String(p.subtitle)}</p> : null}
      </header>
      <div className={`grid gap-4 ${p.density === 'compact' ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
        {(node.children ?? []).map((child) => (
          <CockpitNodeRenderer key={child.id} node={child} bridge={bridge} theme={theme} />
        ))}
      </div>
    </section>
  );
}

function MetricCard({ node, bridge }: { node: CockpitNode; bridge?: CockpitRendererBridge }) {
  const p: NodeProps = node.props ?? {};
  const { data, loading } = useBindingData(p.valueBinding as string | undefined, bridge);
  const bound = p.valueBinding ? getPath(data, p.valuePath as string | undefined) : undefined;
  const value = bound ?? p.value;
  const t = tone(p.tone);
  const tc = TONE_CLASSES[t];
  const delta = p.delta as { value?: unknown; direction?: string; tone?: unknown } | undefined;
  return (
    <div role="group" aria-label={String(p.label ?? 'Metric')} className={`rounded-lg border p-4 ring-1 ${tc.ring} ${tc.bg}`}>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{String(p.label ?? '')}</div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className={`text-2xl font-bold ${tc.text}`}>{loading ? '…' : String(value ?? '—')}</span>
        {p.unit ? <span className="text-sm text-muted-foreground">{String(p.unit)}</span> : null}
      </div>
      {delta ? (
        <div className={`mt-1 text-xs ${TONE_CLASSES[tone(delta.tone)].text}`}>
          {delta.direction === 'up' ? '▲' : delta.direction === 'down' ? '▼' : '▬'} {String(delta.value ?? '')}
        </div>
      ) : null}
    </div>
  );
}

function StatusCardGrid({ node, bridge }: { node: CockpitNode; bridge?: CockpitRendererBridge }) {
  const p: NodeProps = node.props ?? {};
  const { data, loading } = useBindingData(p.itemsBinding as string | undefined, bridge);
  const items = (Array.isArray(data) ? data : (p.items as unknown[]) ?? []) as Array<Record<string, unknown>>;
  const titleField = (p.titleField as string) ?? 'name';
  const valueField = (p.valueField as string) ?? 'value';
  const toneField = p.toneField as string | undefined;
  const cols = Math.min(Math.max(Number(p.columns ?? 3), 1), 6);
  return (
    <div className="rounded-lg border p-4">
      {p.title ? <h2 className="mb-2 text-sm font-semibold">{String(p.title)}</h2> : null}
      <ul role="list" className={`grid gap-2 grid-cols-2 sm:grid-cols-${cols}`}>
        {loading ? (
          <li className="text-sm text-muted-foreground">Loading…</li>
        ) : items.length === 0 ? (
          <li className="text-sm text-muted-foreground">No items.</li>
        ) : (
          items.map((it, i) => {
            const t = tone(toneField ? it[toneField] : it.tone);
            const tc = TONE_CLASSES[t];
            return (
              <li key={i} className={`rounded-md p-2 ring-1 ${tc.ring} ${tc.bg}`}>
                <div className="text-xs text-muted-foreground">{String(it[titleField] ?? '')}</div>
                <div className={`text-sm font-semibold ${tc.text}`}>{String(it[valueField] ?? '')}</div>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}

function DataTable({ node, bridge }: { node: CockpitNode; bridge?: CockpitRendererBridge }) {
  const p: NodeProps = node.props ?? {};
  const { data, loading } = useBindingData(p.rowsBinding as string | undefined, bridge);
  const rows = (Array.isArray(data) ? data : (p.rows as unknown[]) ?? []) as Array<Record<string, unknown>>;
  const columns = (p.columns as Array<{ key: string; label: string; align?: string }>) ?? [];
  const rowKeyField = (p.rowKeyField as string) ?? 'id';
  const rowAction = p.rowAction as string | undefined;
  return (
    <div className="rounded-lg border p-4">
      {p.title ? <h2 className="mb-2 text-sm font-semibold">{String(p.title)}</h2> : null}
      <div className="overflow-x-auto">
        <table role="table" className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs uppercase text-muted-foreground">
              {columns.map((c) => (
                <th key={c.key} className={`px-2 py-1 ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : ''}`}>
                  {c.label}
                </th>
              ))}
              {rowAction ? <th className="px-2 py-1 text-right">Action</th> : null}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="px-2 py-3 text-muted-foreground" colSpan={columns.length + (rowAction ? 1 : 0)}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td className="px-2 py-3 text-muted-foreground" colSpan={columns.length + (rowAction ? 1 : 0)}>{String(p.emptyMessage ?? 'No rows yet.')}</td></tr>
            ) : (
              rows.slice(0, Number(p.pageSize ?? 50)).map((r, i) => (
                <tr key={String(r[rowKeyField] ?? i)} className="border-b last:border-0">
                  {columns.map((c) => (
                    <td key={c.key} className={`px-2 py-1 ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : ''}`}>
                      {String(r[c.key] ?? '')}
                    </td>
                  ))}
                  {rowAction ? (
                    <td className="px-2 py-1 text-right">
                      <button
                        type="button"
                        className="rounded border px-2 py-0.5 text-xs hover:bg-accent"
                        onClick={() => bridge?.executeAction?.(rowAction, { rowKey: r[rowKeyField] })}
                      >
                        {String(p.rowActionLabel ?? 'Run')}
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ChartCard({ node, bridge }: { node: CockpitNode; bridge?: CockpitRendererBridge }) {
  const p: NodeProps = node.props ?? {};
  const { data, loading } = useBindingData(p.seriesBinding as string | undefined, bridge);
  const series = (Array.isArray(data) ? data : (p.series as unknown[]) ?? []) as Array<Record<string, unknown>>;
  const xField = (p.xField as string) ?? 'x';
  const yField = (p.yField as string) ?? 'y';
  const t = tone(p.tone ?? 'info');
  // Lightweight sparkline rendering (no Recharts dependency in the sandbox); a
  // production ChartCard swaps in the platform Recharts wrapper. Renders an SVG
  // polyline so the chart is real + responsive without an extra bundle.
  const ys = series.map((d) => Number(d[yField] ?? 0));
  const max = Math.max(1, ...ys);
  const min = Math.min(0, ...ys);
  const pts = ys
    .map((y, i) => {
      const x = series.length > 1 ? (i / (series.length - 1)) * 100 : 0;
      const ny = max === min ? 50 : 100 - ((y - min) / (max - min)) * 100;
      return `${x},${ny}`;
    })
    .join(' ');
  return (
    <div role="img" aria-label={String(p.title ?? 'Chart')} className="rounded-lg border p-4">
      <h2 className="mb-2 text-sm font-semibold">{String(p.title ?? '')}</h2>
      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : series.length === 0 ? (
        <div className="text-sm text-muted-foreground">No data.</div>
      ) : (
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-32 w-full">
          <polyline points={pts} fill="none" stroke="currentColor" strokeWidth={1.5} className={TONE_CLASSES[t].text} />
        </svg>
      )}
      {p.unit ? <div className="mt-1 text-xs text-muted-foreground">{String(p.unit)}</div> : null}
    </div>
  );
}

function ActionButton({ node, bridge }: { node: CockpitNode; bridge?: CockpitRendererBridge }) {
  const p: NodeProps = node.props ?? {};
  const variant =
    p.tone === 'danger'
      ? 'bg-red-600 text-white hover:bg-red-700'
      : p.tone === 'secondary'
        ? 'border bg-background hover:bg-accent'
        : 'bg-primary text-primary-foreground hover:bg-primary/90';
  const action = p.action as string;
  return (
    <button
      type="button"
      aria-label={String(p.label ?? 'Action')}
      className={`inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium ${variant}`}
      onClick={() => {
        if (p.confirm && typeof window !== 'undefined' && !window.confirm(`${String(p.label)}?`)) return;
        bridge?.executeAction?.(action);
      }}
    >
      {String(p.label ?? '')}
    </button>
  );
}

function AlertBanner({ node }: { node: CockpitNode }) {
  const p: NodeProps = node.props ?? {};
  const t = tone(p.tone ?? 'info');
  const tc = TONE_CLASSES[t];
  return (
    <div role="alert" className={`rounded-lg border p-3 ring-1 ${tc.ring} ${tc.bg}`}>
      <div className={`text-sm font-semibold ${tc.text}`}>{String(p.title ?? '')}</div>
      {p.message ? <div className="mt-0.5 text-sm text-muted-foreground">{String(p.message)}</div> : null}
    </div>
  );
}

function ResidentAgentPanel({ node }: { node: CockpitNode }) {
  const p: NodeProps = node.props ?? {};
  return (
    <aside role="complementary" aria-label={String(p.title ?? 'Argus')} className="rounded-lg border bg-muted/30 p-4">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">A</span>
        <h2 className="text-sm font-semibold">{String(p.title ?? 'Argus')}</h2>
      </div>
      {p.greeting ? <p className="mt-2 text-sm text-muted-foreground">{String(p.greeting)}</p> : null}
      {p.showChips !== false ? (
        <div className="mt-3 flex flex-wrap gap-1">
          <span className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground">Ask Argus…</span>
        </div>
      ) : null}
    </aside>
  );
}

/* ─── Layout: CockpitHeader ─────────────────────────────────────────────── */

function CockpitHeader({ node }: { node: CockpitNode }) {
  const p: NodeProps = node.props ?? {};
  const t = tone(p.tone);
  const tc = TONE_CLASSES[t];
  const showTimestamp = p.showTimestamp !== false;
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!showTimestamp) return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [showTimestamp]);
  return (
    <header
      role="banner"
      aria-label={String(p.title ?? 'Header')}
      className="flex items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3"
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${DOT_CLASSES[t]}`} aria-hidden />
        <h2 className="truncate text-base font-semibold tracking-tight">{String(p.title ?? '')}</h2>
      </div>
      {showTimestamp ? (
        <time className={`shrink-0 text-xs tabular-nums ${tc.text}`} dateTime={now.toISOString()}>
          {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </time>
      ) : null}
    </header>
  );
}

/* ─── Layout: CockpitTabs ───────────────────────────────────────────────── */

function CockpitTabs({ node, bridge, theme }: { node: CockpitNode; bridge?: CockpitRendererBridge; theme?: 'light' | 'dark' }) {
  const p: NodeProps = node.props ?? {};
  const tabs = (Array.isArray(p.tabs) ? p.tabs : []) as Array<{ id: string; label: string }>;
  const children = node.children ?? [];
  const idBase = useId();
  const [active, setActive] = useState<string>(() => {
    const dflt = p.defaultTabId as string | undefined;
    if (dflt && tabs.some((t) => t.id === dflt)) return dflt;
    return tabs[0]?.id ?? '';
  });
  if (tabs.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground" role="tablist" aria-label="Tabs">
        No tabs configured.
      </div>
    );
  }
  const activeIndex = Math.max(0, tabs.findIndex((t) => t.id === active));
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    let next = activeIndex;
    if (e.key === 'ArrowRight') next = (activeIndex + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (activeIndex - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    setActive(tabs[next]!.id);
  };
  return (
    <div className="rounded-lg border bg-card">
      <div role="tablist" aria-label={String(p.label ?? 'Views')} className="flex gap-1 border-b px-2 pt-2" onKeyDown={onKeyDown}>
        {tabs.map((tab) => {
          const selected = tab.id === active;
          return (
            <button
              key={tab.id}
              role="tab"
              id={`${idBase}-tab-${tab.id}`}
              aria-selected={selected}
              aria-controls={`${idBase}-panel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              type="button"
              onClick={() => setActive(tab.id)}
              className={`rounded-t-md px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                selected
                  ? 'border-b-2 border-primary text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {tabs.map((tab, i) => {
        const child = children[i];
        return (
          <div
            key={tab.id}
            role="tabpanel"
            id={`${idBase}-panel-${tab.id}`}
            aria-labelledby={`${idBase}-tab-${tab.id}`}
            hidden={tab.id !== active}
            className="p-4"
          >
            {tab.id === active ? (
              child ? (
                <CockpitNodeRenderer node={child} bridge={bridge} theme={theme} />
              ) : (
                <p className="text-sm text-muted-foreground">Nothing in this view yet.</p>
              )
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Layout: CockpitSidePanel ──────────────────────────────────────────── */

function CockpitSidePanel({ node, bridge, theme }: { node: CockpitNode; bridge?: CockpitRendererBridge; theme?: 'light' | 'dark' }) {
  const p: NodeProps = node.props ?? {};
  const [open, setOpen] = useState<boolean>(p.defaultOpen !== false);
  const panelId = useId();
  const widthClass = p.width === 'sm' ? 'sm:max-w-xs' : p.width === 'lg' ? 'sm:max-w-md' : 'sm:max-w-sm';
  return (
    <aside
      role="complementary"
      aria-label={String(p.title ?? 'Side panel')}
      className={`flex w-full flex-col rounded-lg border bg-card ${open ? widthClass : ''}`}
    >
      <div className="flex items-center justify-between gap-2 border-b px-4 py-2.5">
        <h2 className="truncate text-sm font-semibold">{String(p.title ?? 'Details')}</h2>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((o) => !o)}
          className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="sr-only">{open ? 'Collapse panel' : 'Expand panel'}</span>
          <CircleIcon className={`h-3.5 w-3.5 transition-transform ${open ? '' : 'opacity-40'}`} aria-hidden />
        </button>
      </div>
      <div id={panelId} hidden={!open} className="flex flex-col gap-3 p-4">
        {open ? (
          (node.children ?? []).length > 0 ? (
            (node.children ?? []).map((child) => (
              <CockpitNodeRenderer key={child.id} node={child} bridge={bridge} theme={theme} />
            ))
          ) : (
            <p className="text-sm text-muted-foreground">Select an item to see details.</p>
          )
        ) : null}
      </div>
    </aside>
  );
}

/* ─── Display: StatusPill ───────────────────────────────────────────────── */

function StatusPill({ node }: { node: CockpitNode }) {
  const p: NodeProps = node.props ?? {};
  const t = tone(p.tone);
  return (
    <span
      role="status"
      aria-label={String(p.label ?? 'Status')}
      className={`inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${PILL_CLASSES[t]}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${DOT_CLASSES[t]}`} aria-hidden />
      {String(p.label ?? '')}
    </span>
  );
}

/* ─── Display: DetailPanel ──────────────────────────────────────────────── */

function DetailPanel({ node, bridge }: { node: CockpitNode; bridge?: CockpitRendererBridge }) {
  const p: NodeProps = node.props ?? {};
  const { data, loading, error } = useBindingData(p.recordBinding as string | undefined, bridge);
  const record = (data && typeof data === 'object' ? (data as Record<string, unknown>) : {}) as Record<string, unknown>;
  const fields = (Array.isArray(p.fields) ? p.fields : []) as Array<{ key: string; label: string }>;
  const hasData = data !== undefined && data !== null && Object.keys(record).length > 0;
  return (
    <div role="group" aria-label={String(p.title ?? 'Detail')} className="rounded-lg border bg-card p-4">
      <h2 className="mb-3 text-sm font-semibold">{String(p.title ?? 'Details')}</h2>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : error ? (
        <p className="text-sm text-red-600 dark:text-red-400">Could not load details. {error}</p>
      ) : fields.length === 0 ? (
        <p className="text-sm text-muted-foreground">No fields configured.</p>
      ) : !hasData ? (
        <p className="text-sm text-muted-foreground">No record selected.</p>
      ) : (
        <dl className="grid grid-cols-[minmax(6rem,auto)_1fr] gap-x-4 gap-y-2 text-sm">
          {fields.map((f) => {
            const v = record[f.key];
            return (
              <div key={f.key} className="contents">
                <dt className="text-muted-foreground">{f.label}</dt>
                <dd className="font-medium text-foreground wrap-break-word">
                  {v === undefined || v === null || v === '' ? <span className="text-muted-foreground">—</span> : String(v)}
                </dd>
              </div>
            );
          })}
        </dl>
      )}
    </div>
  );
}

/* ─── Data: KanbanBoard ─────────────────────────────────────────────────── */

function KanbanBoard({ node, bridge }: { node: CockpitNode; bridge?: CockpitRendererBridge }) {
  const p: NodeProps = node.props ?? {};
  const { data, loading, error } = useBindingData(p.rowsBinding as string | undefined, bridge);
  const rows = (Array.isArray(data) ? data : (p.rows as unknown[]) ?? []) as Array<Record<string, unknown>>;
  const groupField = (p.groupField as string) ?? 'status';
  const cardTitleField = (p.cardTitleField as string) ?? 'title';
  const columns = (Array.isArray(p.columns) ? p.columns : []) as string[];
  const byColumn = useMemo(() => {
    const map = new Map<string, Array<Record<string, unknown>>>();
    for (const col of columns) map.set(col, []);
    for (const r of rows) {
      const col = String(r[groupField] ?? '');
      if (map.has(col)) map.get(col)!.push(r);
    }
    return map;
  }, [rows, columns, groupField]);
  return (
    <div className="rounded-lg border bg-card p-4">
      {p.title ? <h2 className="mb-3 text-sm font-semibold">{String(p.title)}</h2> : null}
      {error ? (
        <p className="text-sm text-red-600 dark:text-red-400">Could not load board. {error}</p>
      ) : (
        <ul role="list" className="grid auto-cols-fr grid-flow-col gap-3 overflow-x-auto pb-1" aria-busy={loading}>
          {columns.map((col) => {
            const cards = byColumn.get(col) ?? [];
            return (
              <li key={col} className="min-w-48 rounded-md bg-muted/40 p-2">
                <div className="mb-2 flex items-center justify-between px-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{col}</span>
                  <span className="rounded-full bg-background px-1.5 text-xs tabular-nums text-muted-foreground ring-1 ring-inset ring-border">
                    {cards.length}
                  </span>
                </div>
                <ul className="flex flex-col gap-2">
                  {loading ? (
                    <li className="rounded-md border bg-card p-2 text-xs text-muted-foreground">Loading…</li>
                  ) : cards.length === 0 ? (
                    <li className="rounded-md border border-dashed p-2 text-center text-xs text-muted-foreground">Empty</li>
                  ) : (
                    cards.map((c, i) => (
                      <li key={String(c.id ?? i)} className="rounded-md border bg-card p-2 text-sm shadow-sm">
                        {String(c[cardTitleField] ?? '(untitled)')}
                      </li>
                    ))
                  )}
                </ul>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ─── Data: Timeline ────────────────────────────────────────────────────── */

function Timeline({ node, bridge }: { node: CockpitNode; bridge?: CockpitRendererBridge }) {
  const p: NodeProps = node.props ?? {};
  const { data, loading, error } = useBindingData(p.eventsBinding as string | undefined, bridge);
  const events = (Array.isArray(data) ? data : (p.events as unknown[]) ?? []) as Array<Record<string, unknown>>;
  const tsField = (p.timestampField as string) ?? 'ts';
  const titleField = (p.titleField as string) ?? 'title';
  const toneField = p.toneField as string | undefined;
  return (
    <div className="rounded-lg border bg-card p-4">
      {p.title ? <h2 className="mb-3 text-sm font-semibold">{String(p.title)}</h2> : null}
      <ol role="list" className="relative ml-1.5 flex flex-col gap-4 border-l border-border pl-4">
        {loading ? (
          <li className="-ml-4 list-none text-sm text-muted-foreground">Loading…</li>
        ) : error ? (
          <li className="-ml-4 list-none text-sm text-red-600 dark:text-red-400">Could not load events. {error}</li>
        ) : events.length === 0 ? (
          <li className="-ml-4 list-none text-sm text-muted-foreground">No events recorded.</li>
        ) : (
          events.map((ev, i) => {
            const t = tone(toneField ? ev[toneField] : ev.tone);
            return (
              <li key={i} className="relative">
                <span className={`absolute -left-[1.4rem] top-1 h-2.5 w-2.5 rounded-full ring-2 ring-background ${DOT_CLASSES[t]}`} aria-hidden />
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-medium text-foreground">{String(ev[titleField] ?? '')}</span>
                  <time className="shrink-0 text-xs tabular-nums text-muted-foreground">{relativeTime(ev[tsField])}</time>
                </div>
              </li>
            );
          })
        )}
      </ol>
    </div>
  );
}

/* ─── Input: SearchBar ──────────────────────────────────────────────────── */

function SearchBar({ node, bridge }: { node: CockpitNode; bridge?: CockpitRendererBridge }) {
  const p: NodeProps = node.props ?? {};
  const inputId = useId();
  const targetId = p.targetId as string | undefined;
  const placeholder = String(p.placeholder ?? 'Search…');
  const [value, setValue] = useState('');
  const onChange = (next: string) => {
    setValue(next);
    // Filtering is client-side over a sibling target; the host bridge relays the
    // intent so the operate view can scope the bound table/grid. No action mint.
    bridge?.executeAction?.('__cockpit_filter__', { targetId, query: next });
  };
  return (
    <div role="search" className="relative">
      <label htmlFor={inputId} className="sr-only">
        {placeholder}
      </label>
      <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
      <input
        id={inputId}
        type="search"
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-full rounded-md border bg-background pl-8 pr-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
    </div>
  );
}

/* ─── Input: FilterChips ────────────────────────────────────────────────── */

function FilterChips({ node, bridge }: { node: CockpitNode; bridge?: CockpitRendererBridge }) {
  const p: NodeProps = node.props ?? {};
  const targetId = p.targetId as string | undefined;
  const field = String(p.field ?? '');
  const chips = (Array.isArray(p.chips) ? p.chips : []) as Array<{ label: string; value: string }>;
  const [active, setActive] = useState<Set<string>>(() => new Set());
  const toggle = (value: string) => {
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      bridge?.executeAction?.('__cockpit_filter_chips__', { targetId, field, values: [...next] });
      return next;
    });
  };
  return (
    <div role="group" aria-label={field || 'Filters'} className="flex flex-wrap items-center gap-1.5">
      {chips.map((chip) => {
        const on = active.has(chip.value);
        return (
          <button
            key={chip.value}
            type="button"
            aria-pressed={on}
            onClick={() => toggle(chip.value)}
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              on
                ? 'bg-primary text-primary-foreground ring-primary'
                : 'bg-background text-muted-foreground ring-border hover:bg-accent hover:text-foreground'
            }`}
          >
            {chip.label}
          </button>
        );
      })}
    </div>
  );
}

/* ─── Input: RichTextEditor ─────────────────────────────────────────────── */

function RichTextEditor({ node, bridge }: { node: CockpitNode; bridge?: CockpitRendererBridge }) {
  const p: NodeProps = node.props ?? {};
  const textareaId = useId();
  const stateBinding = p.stateBinding as string | undefined;
  const { data } = useBindingData(stateBinding, bridge);
  const maxLength = Math.min(Math.max(Number(p.maxLength ?? 4000), 1), 20000);
  const [value, setValue] = useState('');
  const initialized = useRef(false);
  useEffect(() => {
    // Hydrate once from the bound state; user edits then own the field.
    if (initialized.current) return;
    if (typeof data === 'string') {
      setValue(data.slice(0, maxLength));
      initialized.current = true;
    } else if (data && typeof data === 'object' && typeof (data as { text?: unknown }).text === 'string') {
      setValue(((data as { text: string }).text).slice(0, maxLength));
      initialized.current = true;
    }
  }, [data, maxLength]);
  const apply = (wrap: string) => {
    // Markdown-subset affordance (no raw HTML — the value is plain text the host
    // renders through MarkdownRenderer). Appends the marker; keeps it simple +
    // deterministic for the constrained editor.
    setValue((v) => `${v}${wrap}`);
  };
  return (
    <div className="rounded-lg border bg-card p-3">
      <label htmlFor={textareaId} className="mb-1.5 block text-sm font-medium">
        {String(p.label ?? 'Note')}
      </label>
      <div className="mb-1.5 flex items-center gap-1" role="toolbar" aria-label="Formatting">
        <button type="button" aria-label="Bold" onClick={() => apply('**bold**')} className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <BoldIcon className="h-3.5 w-3.5" aria-hidden />
        </button>
        <button type="button" aria-label="Italic" onClick={() => apply('_italic_')} className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <ItalicIcon className="h-3.5 w-3.5" aria-hidden />
        </button>
        <button type="button" aria-label="Bullet list" onClick={() => apply('\n- ')} className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <ListIcon className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
      <textarea
        id={textareaId}
        value={value}
        maxLength={maxLength}
        placeholder={p.placeholder ? String(p.placeholder) : undefined}
        aria-label={String(p.label ?? 'Note')}
        onChange={(e) => {
          setValue(e.target.value);
          if (stateBinding) bridge?.executeAction?.('__cockpit_state__', { binding: stateBinding, text: e.target.value });
        }}
        className="min-h-20 w-full resize-y rounded-md border bg-background p-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <div className="mt-1 text-right text-xs tabular-nums text-muted-foreground">
        {value.length} / {maxLength}
      </div>
    </div>
  );
}

/* ─── Action: InlineActions ─────────────────────────────────────────────── */

function InlineActions({ node, bridge }: { node: CockpitNode; bridge?: CockpitRendererBridge }) {
  const p: NodeProps = node.props ?? {};
  const actions = (Array.isArray(p.actions) ? p.actions : []) as Array<{ label: string; action: string; tone?: string }>;
  return (
    <div role="group" aria-label="Actions" className="flex flex-wrap items-center gap-1.5">
      {actions.map((a, i) => {
        const variant =
          a.tone === 'danger'
            ? 'bg-red-600 text-white hover:bg-red-700'
            : a.tone === 'primary'
              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
              : 'border bg-background hover:bg-accent';
        return (
          <button
            key={`${a.action}-${i}`}
            type="button"
            aria-label={a.label}
            onClick={() => bridge?.executeAction?.(a.action)}
            className={`inline-flex items-center justify-center rounded-md px-2.5 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${variant}`}
          >
            {a.label}
          </button>
        );
      })}
    </div>
  );
}

/* ─── Live: LiveIndicator ───────────────────────────────────────────────── */

function LiveIndicator({ node, bridge }: { node: CockpitNode; bridge?: CockpitRendererBridge }) {
  const p: NodeProps = node.props ?? {};
  const { lastUpdated, loading, error } = useSubscribeData(p.subscribeBinding as string | undefined, bridge);
  const [, force] = useState(0);
  useEffect(() => {
    // Tick the "x ago" label even between polls so freshness stays honest.
    const id = setInterval(() => force((n) => n + 1), 5000);
    return () => clearInterval(id);
  }, []);
  const live = !error && (lastUpdated !== null || !p.subscribeBinding);
  const dot = error ? 'critical' : live ? 'success' : 'normal';
  const text = error
    ? 'Disconnected'
    : loading && lastUpdated === null
      ? 'Connecting…'
      : lastUpdated !== null
        ? `Updated ${relativeTime(lastUpdated)}`
        : String(p.label ?? 'Live');
  return (
    <span
      role="status"
      aria-label={String(p.label ?? 'Live')}
      aria-live="polite"
      className={`inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${PILL_CLASSES[dot]}`}
    >
      <span className={`relative flex h-1.5 w-1.5`}>
        {live ? <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${DOT_CLASSES[dot]} opacity-75`} /> : null}
        <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${DOT_CLASSES[dot]}`} />
      </span>
      {String(p.label ?? 'Live')}
      {text !== String(p.label ?? 'Live') ? <span className="text-muted-foreground">· {text}</span> : null}
    </span>
  );
}

/* ─── Live: NotificationFeed ────────────────────────────────────────────── */

function NotificationFeed({ node, bridge }: { node: CockpitNode; bridge?: CockpitRendererBridge }) {
  const p: NodeProps = node.props ?? {};
  // Seed from the read binding, then keep fresh over the subscribe binding.
  const seed = useBindingData(p.feedBinding as string | undefined, bridge);
  const live = useSubscribeData(p.subscribeBinding as string | undefined, bridge);
  const data = live.lastUpdated !== null ? live.data : seed.data;
  const loading = seed.loading && live.lastUpdated === null;
  const error = seed.error ?? live.error;
  const items = (Array.isArray(data) ? data : []) as Array<Record<string, unknown>>;
  const titleField = (p.titleField as string) ?? 'title';
  const tsField = (p.timestampField as string) ?? 'ts';
  const maxItems = Math.min(Math.max(Number(p.maxItems ?? 50), 1), 200);
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-2 flex items-center gap-2">
        <BellIcon className="h-4 w-4 text-muted-foreground" aria-hidden />
        <h2 className="text-sm font-semibold">{String(p.title ?? 'Notifications')}</h2>
      </div>
      <ul role="log" aria-label={String(p.title ?? 'Notifications')} aria-live="polite" aria-busy={loading} className="flex max-h-72 flex-col gap-1.5 overflow-y-auto">
        {loading ? (
          <li className="text-sm text-muted-foreground">Loading…</li>
        ) : error ? (
          <li className="text-sm text-red-600 dark:text-red-400">Could not load feed. {error}</li>
        ) : items.length === 0 ? (
          <li className="flex flex-col items-center gap-1 py-6 text-center text-sm text-muted-foreground">
            <InboxIcon className="h-5 w-5 opacity-60" aria-hidden />
            Nothing new.
          </li>
        ) : (
          items.slice(0, maxItems).map((it, i) => (
            <li key={String(it.id ?? i)} className="flex items-baseline justify-between gap-2 rounded-md border-l-2 border-border bg-muted/30 px-2.5 py-1.5 text-sm">
              <span className="min-w-0 wrap-break-word text-foreground">{String(it[titleField] ?? '')}</span>
              <time className="shrink-0 text-xs tabular-nums text-muted-foreground">{relativeTime(it[tsField])}</time>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

/* ═══ feat/cockpit comprehensive palette ════════════════════════════════ */

/* ─── Data: Chart (line/area/bar/pie/donut/gauge/sparkline/scatter) ──────── */

function Chart({ node, bridge }: { node: CockpitNode; bridge?: CockpitRendererBridge }) {
  const p: NodeProps = node.props ?? {};
  const { data, loading, error } = useBindingData(p.dataBinding as string | undefined, bridge);
  const rows = (Array.isArray(data) ? data : (p.data as unknown[]) ?? []) as Array<Record<string, unknown>>;
  const chartType = String(p.chartType ?? 'line');
  const xKey = (p.xKey as string) ?? 'x';
  const yKeys = (Array.isArray(p.yKeys) && p.yKeys.length > 0 ? p.yKeys : ['y']) as string[];
  const legend = p.legend !== false;
  const title = p.title ? String(p.title) : undefined;

  const body = () => {
    if (loading) return <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>;
    if (error) return <div className="py-8 text-center text-sm text-red-600 dark:text-red-400">Could not load chart.</div>;
    if (rows.length === 0) return <div className="py-8 text-center text-sm text-muted-foreground">No data.</div>;

    if (chartType === 'pie' || chartType === 'donut') {
      return (
        <ResponsiveContainer width="100%" height={180}>
          <PieChart>
            <Pie
              data={rows.map((r) => ({ name: String(r[xKey] ?? ''), value: Number(r[yKeys[0]!] ?? 0) }))}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={70}
              innerRadius={chartType === 'donut' ? 42 : 0}
            >
              {rows.map((_, i) => (
                <Cell key={i} fill={colorAt(p.colors, i)} />
              ))}
            </Pie>
            {legend ? <RechartsLegend /> : null}
            <RechartsTooltip />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === 'gauge') {
      const value = Number(rows[0]?.[yKeys[0]!] ?? 0);
      const max = Number(p.max ?? 100);
      const pct = Math.max(0, Math.min(100, max > 0 ? (value / max) * 100 : 0));
      return (
        <div className="relative">
          <ResponsiveContainer width="100%" height={140}>
            <RadialBarChart
              innerRadius="70%"
              outerRadius="100%"
              data={[{ name: 'value', value: pct, fill: colorAt(p.colors, 0) }]}
              startAngle={180}
              endAngle={0}
            >
              <RadialBar background dataKey="value" cornerRadius={6} />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-x-0 bottom-6 text-center text-2xl font-bold text-foreground">
            {value}
            {p.unit ? <span className="text-sm text-muted-foreground"> {String(p.unit)}</span> : null}
          </div>
        </div>
      );
    }

    if (chartType === 'sparkline') {
      return (
        <ResponsiveContainer width="100%" height={48}>
          <LineChart data={rows} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
            <Line type="monotone" dataKey={yKeys[0]!} stroke={colorAt(p.colors, 0)} strokeWidth={1.5} dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === 'scatter') {
      return (
        <ResponsiveContainer width="100%" height={180}>
          <ScatterChart margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey={xKey} fontSize={10} tickLine={false} />
            <YAxis fontSize={10} tickLine={false} width={32} />
            <RechartsTooltip />
            {legend ? <RechartsLegend /> : null}
            {yKeys.map((k, i) => (
              <Scatter key={k} name={k} data={rows} dataKey={k} fill={colorAt(p.colors, i)} />
            ))}
          </ScatterChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === 'bar') {
      return (
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border" />
            <XAxis dataKey={xKey} fontSize={10} tickLine={false} />
            <YAxis fontSize={10} tickLine={false} width={32} />
            <RechartsTooltip />
            {legend ? <RechartsLegend /> : null}
            {yKeys.map((k, i) => (
              <Bar key={k} dataKey={k} fill={colorAt(p.colors, i)} radius={[2, 2, 0, 0]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === 'area') {
      return (
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={rows} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border" />
            <XAxis dataKey={xKey} fontSize={10} tickLine={false} />
            <YAxis fontSize={10} tickLine={false} width={32} />
            <RechartsTooltip />
            {legend ? <RechartsLegend /> : null}
            {yKeys.map((k, i) => (
              <Area key={k} type="monotone" dataKey={k} stroke={colorAt(p.colors, i)} fill={colorAt(p.colors, i)} fillOpacity={0.18} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      );
    }

    // default: line
    return (
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={rows} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border" />
          <XAxis dataKey={xKey} fontSize={10} tickLine={false} />
          <YAxis fontSize={10} tickLine={false} width={32} />
          <RechartsTooltip />
          {legend ? <RechartsLegend /> : null}
          {yKeys.map((k, i) => (
            <Line key={k} type="monotone" dataKey={k} stroke={colorAt(p.colors, i)} strokeWidth={2} dot={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div role="img" aria-label={title ?? `${chartType} chart`} className="rounded-lg border bg-card p-4">
      {title ? <h2 className="mb-2 text-sm font-semibold">{title}</h2> : null}
      {body()}
      {p.unit && chartType !== 'gauge' ? <div className="mt-1 text-xs text-muted-foreground">{String(p.unit)}</div> : null}
    </div>
  );
}

/* ─── Data: MapView (air-gap-safe inline-SVG geospatial) ─────────────────── */

function MapView({ node, bridge }: { node: CockpitNode; bridge?: CockpitRendererBridge }) {
  const p: NodeProps = node.props ?? {};
  const { data, loading, error } = useBindingData(p.pointsBinding as string | undefined, bridge);
  const rawPts = (Array.isArray(data) ? data : (p.points as unknown[]) ?? []) as Array<Record<string, unknown>>;
  const latKey = (p.latKey as string) ?? 'lat';
  const lngKey = (p.lngKey as string) ?? 'lng';
  const labelKey = (p.labelKey as string) ?? 'label';
  const statusKey = p.statusKey as string | undefined;
  const regions = (Array.isArray(p.regions) ? p.regions : []) as Array<{ tone?: string; ring: Array<[number, number]>; label?: string }>;
  const title = p.title ? String(p.title) : undefined;

  const W = 360;
  const H = 200;
  const pad = 14;

  const pts = rawPts
    .map((pt) => ({
      lat: Number(pt[latKey]),
      lng: Number(pt[lngKey]),
      label: String(pt[labelKey] ?? ''),
      status: tone(statusKey ? pt[statusKey] : pt.status),
    }))
    .filter((q) => Number.isFinite(q.lat) && Number.isFinite(q.lng));

  const bbox = useMemo(() => {
    if (Array.isArray(p.bbox)) return p.bbox as [number, number, number, number];
    const lngs = [...pts.map((q) => q.lng), ...regions.flatMap((r) => r.ring.map((c) => c[0]))];
    const lats = [...pts.map((q) => q.lat), ...regions.flatMap((r) => r.ring.map((c) => c[1]))];
    if (lngs.length === 0) return [-180, -90, 180, 90] as [number, number, number, number];
    let minLng = Math.min(...lngs), maxLng = Math.max(...lngs), minLat = Math.min(...lats), maxLat = Math.max(...lats);
    const dLng = maxLng - minLng || 1, dLat = maxLat - minLat || 1;
    minLng -= dLng * 0.12; maxLng += dLng * 0.12; minLat -= dLat * 0.12; maxLat += dLat * 0.12;
    return [minLng, minLat, maxLng, maxLat] as [number, number, number, number];
  }, [p.bbox, pts, regions]);

  const [minLng, minLat, maxLng, maxLat] = bbox;
  const spanLng = maxLng - minLng || 1;
  const spanLat = maxLat - minLat || 1;
  const project = (lat: number, lng: number): [number, number] => [
    pad + ((lng - minLng) / spanLng) * (W - 2 * pad),
    pad + (1 - (lat - minLat) / spanLat) * (H - 2 * pad),
  ];

  return (
    <div role="img" aria-label={title ?? 'Map'} className="rounded-lg border bg-card p-4">
      {title ? <h2 className="mb-2 text-sm font-semibold">{title}</h2> : null}
      {loading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : error ? (
        <div className="py-8 text-center text-sm text-red-600 dark:text-red-400">Could not load locations.</div>
      ) : pts.length === 0 && regions.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">No locations.</div>
      ) : (
        <>
          <svg viewBox={`0 0 ${W} ${H}`} className="block w-full rounded-md bg-muted/30">
            {/* graticule grid — a clean static map without external tiles */}
            {[1, 2, 3, 4, 5].map((g) => {
              const x = pad + (g / 6) * (W - 2 * pad);
              return <line key={`vx${g}`} x1={x} y1={pad} x2={x} y2={H - pad} className="stroke-border" strokeWidth={0.5} />;
            })}
            {[1, 2, 3].map((g) => {
              const y = pad + (g / 4) * (H - 2 * pad);
              return <line key={`hz${g}`} x1={pad} y1={y} x2={W - pad} y2={y} className="stroke-border" strokeWidth={0.5} />;
            })}
            {regions.map((r, ri) => {
              const hex = TONE_HEX[tone(r.tone)];
              const points = r.ring.map(([lng, lat]) => project(lat, lng).join(',')).join(' ');
              return <polygon key={ri} points={points} fill={hex} fillOpacity={0.15} stroke={hex} strokeWidth={1.2} />;
            })}
            {pts.map((q, i) => {
              const [x, y] = project(q.lat, q.lng);
              const hex = TONE_HEX[q.status];
              return (
                <g key={i}>
                  <circle cx={x} cy={y} r={7} fill={hex} fillOpacity={0.25} />
                  <circle cx={x} cy={y} r={4} fill={hex} className="stroke-background" strokeWidth={1.5} />
                  <title>{q.label}</title>
                </g>
              );
            })}
          </svg>
          {pts.length > 0 ? (
            <ul role="list" className="mt-2 flex flex-wrap gap-2.5">
              {pts.slice(0, 12).map((q, i) => (
                <li key={i} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className={`h-2 w-2 rounded-full ${DOT_CLASSES[q.status]}`} aria-hidden />
                  {q.label}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}
    </div>
  );
}

/* ─── Display: Icon ─────────────────────────────────────────────────────── */

function Icon({ node }: { node: CockpitNode }) {
  const p: NodeProps = node.props ?? {};
  const LucideGlyph = lucideForName(p.name);
  const px = ICON_PX[String(p.size ?? 'md')] ?? 20;
  const t = tone(p.tone);
  const label = p.label ? String(p.label) : undefined;
  return (
    <span className="inline-flex items-center justify-center leading-none">
      <LucideGlyph
        width={px}
        height={px}
        className={TONE_CLASSES[t].text}
        role={label ? 'img' : undefined}
        aria-label={label}
        aria-hidden={label ? undefined : true}
      />
    </span>
  );
}

/* ─── Display: Badge ────────────────────────────────────────────────────── */

function Badge({ node }: { node: CockpitNode }) {
  const p: NodeProps = node.props ?? {};
  const t = tone(p.tone);
  const variant = String(p.variant ?? 'soft');
  const LucideGlyph = p.icon ? lucideForName(p.icon) : null;
  const cls =
    variant === 'solid'
      ? `${DOT_CLASSES[t]} text-white`
      : variant === 'outline'
        ? `bg-transparent ring-1 ring-inset ${PILL_CLASSES[t].split(' ').filter((c) => c.startsWith('text-') || c.startsWith('ring-') || c.startsWith('dark:text-') || c.startsWith('dark:ring-')).join(' ')}`
        : PILL_CLASSES[t];
  return (
    <span role="status" aria-label={String(p.label ?? 'Badge')} className={`inline-flex w-fit items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold ${cls}`}>
      {LucideGlyph ? <LucideGlyph className="h-3 w-3" aria-hidden /> : null}
      {String(p.label ?? '')}
    </span>
  );
}

/* ─── Display: ProgressBar ──────────────────────────────────────────────── */

function ProgressBar({ node, bridge }: { node: CockpitNode; bridge?: CockpitRendererBridge }) {
  const p: NodeProps = node.props ?? {};
  const { data } = useBindingData(p.valueBinding as string | undefined, bridge);
  const bound = p.valueBinding ? getPath(data, p.valuePath as string | undefined) : undefined;
  const value = Number(bound ?? p.value ?? 0);
  const max = Math.max(1, Number(p.max ?? 100));
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const t = tone(p.tone ?? 'info');
  return (
    <div className="w-full">
      {p.label || p.showValue !== false ? (
        <div className="mb-1 flex items-baseline justify-between">
          {p.label ? <span className="text-xs font-medium text-foreground">{String(p.label)}</span> : <span />}
          {p.showValue !== false ? <span className="text-xs tabular-nums text-muted-foreground">{Math.round(value)} / {max}</span> : null}
        </div>
      ) : null}
      <div
        role="progressbar"
        aria-label={String(p.label ?? 'Progress')}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
        className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800"
      >
        <div className={`h-full rounded-full transition-all ${DOT_CLASSES[t]}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/* ─── Display: Stat (big number + delta) ────────────────────────────────── */

function Stat({ node, bridge }: { node: CockpitNode; bridge?: CockpitRendererBridge }) {
  const p: NodeProps = node.props ?? {};
  const { data } = useBindingData(p.valueBinding as string | undefined, bridge);
  const bound = p.valueBinding ? getPath(data, p.valuePath as string | undefined) : undefined;
  const value = bound ?? p.value;
  const t = tone(p.tone);
  const tc = TONE_CLASSES[t];
  const delta = p.delta as { value?: unknown; direction?: string; tone?: unknown } | undefined;
  const LucideGlyph = p.icon ? lucideForName(p.icon) : null;
  return (
    <div role="group" aria-label={String(p.label ?? 'Stat')} className="flex items-center gap-3">
      {LucideGlyph ? (
        <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1 ${tc.bg} ${tc.ring}`}>
          <LucideGlyph className={`h-5 w-5 ${tc.text}`} aria-hidden />
        </span>
      ) : null}
      <div className="flex min-w-0 flex-col">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{String(p.label ?? '')}</div>
        <div className="flex items-baseline gap-1">
          <span className={`text-2xl font-bold ${tc.text}`}>{String(value ?? '—')}</span>
          {p.unit ? <span className="text-xs text-muted-foreground">{String(p.unit)}</span> : null}
        </div>
        {delta ? (
          <div className={`text-xs ${TONE_CLASSES[tone(delta.tone)].text}`}>
            {delta.direction === 'up' ? '▲' : delta.direction === 'down' ? '▼' : '▬'} {String(delta.value ?? '')}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ─── Layout: Divider ───────────────────────────────────────────────────── */

function Divider({ node }: { node: CockpitNode }) {
  const p: NodeProps = node.props ?? {};
  const spacing = p.spacing === 'lg' ? 'my-5' : p.spacing === 'sm' ? 'my-1.5' : 'my-3';
  if (!p.label) return <hr role="separator" className={`w-full border-border ${spacing}`} />;
  return (
    <div role="separator" aria-label={String(p.label)} className={`flex items-center gap-3 ${spacing}`}>
      <span className="h-px flex-1 bg-border" />
      <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{String(p.label)}</span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

/* ─── Action: ButtonGroup ───────────────────────────────────────────────── */

function ButtonGroup({ node, bridge }: { node: CockpitNode; bridge?: CockpitRendererBridge }) {
  const p: NodeProps = node.props ?? {};
  const buttons = (Array.isArray(p.buttons) ? p.buttons : []) as Array<{ label: string; action: string; icon?: string; tone?: string }>;
  return (
    <div role="group" aria-label="Actions" className="inline-flex w-fit overflow-hidden rounded-md border">
      {buttons.map((b, i) => {
        const variant =
          b.tone === 'danger' ? 'bg-red-600 text-white hover:bg-red-700' : b.tone === 'primary' ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-card text-foreground hover:bg-accent';
        const LucideGlyph = b.icon ? lucideForName(b.icon) : null;
        return (
          <button
            key={`${b.action}-${i}`}
            type="button"
            aria-label={b.label}
            onClick={() => bridge?.executeAction?.(b.action)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${i > 0 ? 'border-l' : ''} ${variant}`}
          >
            {LucideGlyph ? <LucideGlyph className="h-3.5 w-3.5" aria-hidden /> : null}
            {b.label}
          </button>
        );
      })}
    </div>
  );
}

/* ─── Input: ToggleGroup ────────────────────────────────────────────────── */

function ToggleGroup({ node, bridge }: { node: CockpitNode; bridge?: CockpitRendererBridge }) {
  const p: NodeProps = node.props ?? {};
  const options = (Array.isArray(p.options) ? p.options : []) as Array<{ label: string; value: string }>;
  const targetId = p.targetId as string | undefined;
  const field = String(p.field ?? 'range');
  const [active, setActive] = useState<string>(() => {
    const dflt = p.defaultValue as string | undefined;
    if (dflt && options.some((o) => o.value === dflt)) return dflt;
    return options[0]?.value ?? '';
  });
  const activeIndex = Math.max(0, options.findIndex((o) => o.value === active));
  const select = (value: string) => {
    setActive(value);
    bridge?.executeAction?.('__cockpit_toggle__', { targetId, field, value });
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next = e.key === 'ArrowRight' ? (activeIndex + 1) % options.length : (activeIndex - 1 + options.length) % options.length;
    select(options[next]!.value);
  };
  return (
    <div role="radiogroup" aria-label={String(p.label ?? field)} onKeyDown={onKeyDown} className="inline-flex w-fit gap-0.5 rounded-lg border bg-muted/40 p-0.5">
      {options.map((o) => {
        const on = o.value === active;
        return (
          <button
            key={o.value}
            role="radio"
            type="button"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            onClick={() => select(o.value)}
            className={`rounded-md px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              on ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* ─── Data: TreeList (collapsible hierarchy) ────────────────────────────── */

function TreeNode({
  node: n,
  depth,
  labelField,
  childrenField,
  toneField,
  defaultExpanded,
}: {
  node: Record<string, unknown>;
  depth: number;
  labelField: string;
  childrenField: string;
  toneField?: string;
  defaultExpanded: boolean;
}) {
  const kids = (Array.isArray(n[childrenField]) ? n[childrenField] : []) as Array<Record<string, unknown>>;
  const [open, setOpen] = useState(defaultExpanded);
  const hasKids = kids.length > 0;
  const t = toneField && n[toneField] ? tone(n[toneField]) : null;
  return (
    <li role="treeitem" aria-expanded={hasKids ? open : undefined}>
      <div
        className={`flex items-center gap-1.5 py-0.5 ${hasKids ? 'cursor-pointer' : ''}`}
        style={{ paddingLeft: `${depth * 14}px` }}
        onClick={hasKids ? () => setOpen((o) => !o) : undefined}
      >
        <span className="w-3 shrink-0 text-muted-foreground">
          {hasKids ? open ? <ChevronDownIcon className="h-3 w-3" aria-hidden /> : <ChevronRightIcon className="h-3 w-3" aria-hidden /> : null}
        </span>
        {t ? <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${DOT_CLASSES[t]}`} aria-hidden /> : null}
        <span className="text-sm text-foreground">{String(n[labelField] ?? '')}</span>
      </div>
      {hasKids && open ? (
        <ul role="group" className="m-0 list-none p-0">
          {kids.map((k, i) => (
            <TreeNode key={i} node={k} depth={depth + 1} labelField={labelField} childrenField={childrenField} toneField={toneField} defaultExpanded={defaultExpanded} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function TreeList({ node, bridge }: { node: CockpitNode; bridge?: CockpitRendererBridge }) {
  const p: NodeProps = node.props ?? {};
  const { data, loading, error } = useBindingData(p.nodesBinding as string | undefined, bridge);
  const nodes = (Array.isArray(data) ? data : (p.nodes as unknown[]) ?? []) as Array<Record<string, unknown>>;
  const labelField = (p.labelField as string) ?? 'label';
  const childrenField = (p.childrenField as string) ?? 'children';
  const toneField = p.toneField as string | undefined;
  const defaultExpanded = p.defaultExpanded !== false;
  return (
    <div className="rounded-lg border bg-card p-4">
      {p.title ? <h2 className="mb-3 text-sm font-semibold">{String(p.title)}</h2> : null}
      <ul role="tree" aria-label={String(p.title ?? 'Tree')} className="m-0 list-none p-0">
        {loading ? (
          <li className="text-sm text-muted-foreground">Loading…</li>
        ) : error ? (
          <li className="text-sm text-red-600 dark:text-red-400">Could not load tree.</li>
        ) : nodes.length === 0 ? (
          <li className="text-sm text-muted-foreground">No items.</li>
        ) : (
          nodes.map((n, i) => (
            <TreeNode key={i} node={n} depth={0} labelField={labelField} childrenField={childrenField} toneField={toneField} defaultExpanded={defaultExpanded} />
          ))
        )}
      </ul>
    </div>
  );
}

/* ─── Stub renderer (contract+stub components) ──────────────────────────── */

function StubComponent({ node }: { node: CockpitNode }) {
  // TODO: full renderer for this catalog component (contract is complete; this
  // is a labelled stub so the tree still renders end-to-end). Renders a labelled
  // placeholder, NEVER raw user HTML.
  const p = node.props ?? {};
  const label = (p.title as string) ?? (p.label as string) ?? node.component;
  return (
    <div className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
      <span className="font-medium">{node.component}</span>
      {label && label !== node.component ? <span> — {String(label)}</span> : null}
    </div>
  );
}

/* ─── The renderer registry (slug → component) ──────────────────────────── */

type RendererFn = (args: { node: CockpitNode; bridge?: CockpitRendererBridge; theme?: 'light' | 'dark' }) => React.ReactElement;

const RENDERERS: Record<string, RendererFn> = {
  // Core 8 (Phase 6)
  CockpitContainer,
  MetricCard,
  StatusCardGrid,
  DataTable,
  ChartCard,
  ActionButton,
  AlertBanner,
  ResidentAgentPanel,
  // Newly implemented (feat/cockpit) — full renderers, no stubs
  CockpitHeader,
  CockpitTabs,
  CockpitSidePanel,
  StatusPill,
  DetailPanel,
  KanbanBoard,
  Timeline,
  SearchBar,
  FilterChips,
  RichTextEditor,
  InlineActions,
  LiveIndicator,
  NotificationFeed,
  // feat/cockpit comprehensive palette — charts, maps, icons + controls
  Chart,
  MapView,
  Icon,
  Badge,
  ProgressBar,
  Stat,
  Divider,
  ButtonGroup,
  ToggleGroup,
  TreeList,
};

/* ─── The node renderer ─────────────────────────────────────────────────── */

function CockpitNodeRenderer({ node, bridge, theme }: { node: CockpitNode; bridge?: CockpitRendererBridge; theme?: 'light' | 'dark' }) {
  // Catalog membership is the renderer's last-line "only catalog components"
  // gate — an unknown component renders a visible placeholder, never raw HTML.
  if (!isCockpitComponent(node.component)) {
    return (
      <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-300">
        Unknown component &lsquo;{node.component}&rsquo; (not in the cockpit-library catalog).
      </div>
    );
  }
  const Renderer = RENDERERS[node.component] ?? StubComponent;
  return <Renderer node={node} bridge={bridge} theme={theme} />;
}

/* ─── The public renderer ───────────────────────────────────────────────── */

/**
 * Render a validated CockpitSpec → DOM. The root must be a CockpitContainer
 * (the schema enforces this); the renderer recurses through `children`.
 *
 * Multi-page: when `spec.pages` declares pages beyond `root`, a page-nav tablist
 * is rendered above the active page. Single-page specs render `root` directly
 * with NO nav chrome (identical to the pre-multipage behaviour).
 */
export function CockpitRenderer({ spec, bridge, theme }: CockpitRendererProps): React.ReactElement {
  const pages = useMemo(() => getCockpitPages(spec), [spec]);
  return (
    <div data-cockpit-spec-version={spec.specVersion} className={theme === 'dark' ? 'dark' : undefined}>
      {pages.length <= 1 ? (
        <CockpitNodeRenderer node={spec.root} bridge={bridge} theme={theme} />
      ) : (
        <CockpitPagesView pages={pages} bridge={bridge} theme={theme} />
      )}
    </div>
  );
}

/**
 * Multi-page nav: a `role=tablist` of page tabs above a `role=tabpanel`. The
 * active page's tree mounts on switch (so a page's binding hooks only poll while
 * visible). Keyboard: ←/→ (+ Home/End) move tabs, roving tabindex.
 */
function CockpitPagesView({
  pages,
  bridge,
  theme,
}: {
  pages: ResolvedCockpitPage[];
  bridge?: CockpitRendererBridge;
  theme?: 'light' | 'dark';
}): React.ReactElement {
  const [active, setActive] = useState(0);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const baseId = useId();

  const onKey = (e: React.KeyboardEvent, i: number): void => {
    let next = -1;
    if (e.key === 'ArrowRight') next = (i + 1) % pages.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + pages.length) % pages.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = pages.length - 1;
    if (next >= 0) {
      e.preventDefault();
      setActive(next);
      tabRefs.current[next]?.focus();
    }
  };

  const current = pages[Math.min(active, pages.length - 1)];

  return (
    <div className="flex flex-col min-h-full" data-testid="cockpit-pages">
      <div
        role="tablist"
        aria-label="Cockpit pages"
        className="flex gap-0.5 px-3 pt-2 border-b border-slate-200 dark:border-slate-800 overflow-x-auto bg-white dark:bg-slate-900 shrink-0"
      >
        {pages.map((page, i) => {
          const on = i === active;
          const Icon = page.icon ? lucideForName(page.icon) : null;
          return (
            <button
              key={page.id}
              ref={(node) => {
                tabRefs.current[i] = node;
              }}
              id={`${baseId}-tab-${i}`}
              type="button"
              role="tab"
              data-page-id={page.id}
              aria-selected={on}
              aria-controls={`${baseId}-panel`}
              tabIndex={on ? 0 : -1}
              onClick={() => setActive(i)}
              onKeyDown={(e) => onKey(e, i)}
              className={[
                'inline-flex items-center gap-1.5 px-3.5 py-2 text-[13px] whitespace-nowrap rounded-t-md border-b-2 transition-colors',
                on
                  ? 'border-[#1890ff] text-[#1890ff] dark:text-white font-semibold'
                  : 'border-transparent text-slate-500 dark:text-slate-400 font-medium hover:text-slate-700 dark:hover:text-slate-200',
              ].join(' ')}
            >
              {Icon ? <Icon className="h-[15px] w-[15px]" aria-hidden /> : null}
              <span>{page.title}</span>
            </button>
          );
        })}
      </div>
      <div
        id={`${baseId}-panel`}
        role="tabpanel"
        aria-labelledby={`${baseId}-tab-${active}`}
        className="flex-1 min-h-0 overflow-auto"
      >
        {/* key forces a fresh mount per page so binding hooks re-run on switch. */}
        <CockpitNodeRenderer key={current.id} node={current.root} bridge={bridge} theme={theme} />
      </div>
    </div>
  );
}

/** The slugs the renderer fully implements (the core 8). */
export const RENDERED_BY_REGISTRY = Object.keys(RENDERERS);
